<script setup lang="ts">
import { computed } from 'vue';
import ProgressRing from './ProgressRing.vue';
import { tagLabel } from '@/utils/credentialTypes';
import { formatDate } from '@/utils/formatters';
import DeliveryTag from '@/components/common/DeliveryTag.vue';
import TypeTag from '@/components/common/TypeTag.vue';

/**
 * CourseCredit - Credit information for a course.
 *
 * - `label`: Formatted string with abbreviation for chip display (e.g., "NY CLE 1.0")
 * - `jurisdiction`: Full name for flyout display (e.g., "New York")
 */
export interface CourseCredit {
  /** Pre-formatted label with abbreviation for chip display (e.g., "NY CLE 1.0") */
  label: string;
  userMatch?: boolean;
  /** Full jurisdiction name for flyout display (e.g., "New York") */
  jurisdiction?: string;
  creditType?: string;
  amount?: number;
  /** Credential type code for this accreditation (cle, cpe, iapp, ea, or admin-created). */
  credentialCode?: string;
}

const props = withDefaults(
  defineProps<{
    id: string | number;
    title: string;
    progress?: number;
    duration: string;
    dateTime?: string;
    practiceAreas?: string[];
    /**
     * Credential codes the row should render as chips. One chip per code, ordered
     * by credential-type sortOrder upstream. Empty for non-credit content.
     */
    credentialCodes: string[];
    /**
     * Fallback chip kind when `credentialCodes` is empty (e.g. `'microlearning'`,
     * `'skills'`, `'pathway'`). Drives the single non-credit chip's CSS class.
     */
    learningObjectTypeCode?: string;
    credits?: CourseCredit[];
    actionLabel: string;
    actionUrl: string;
    compact?: boolean;
    courseLinkPrefix?: string;
    deliveryMode?: 'OnDemand' | 'Live';
    /** When an admin assigned the course. Hidden when null. */
    assignedAt?: string | null;
    /** Original recording date (LearningObject.OriginalLiveDate). Hidden when null. */
    recordedDate?: string | null;
  }>(),
  {
    courseLinkPrefix: '/course',
  }
);

const emit = defineEmits<{
  viewJurisdictions: [learningObjectId: string | number, credits: CourseCredit[]];
}>();

// Separate user matches and other credits for display
const userMatchCredits = computed(() =>
  props.credits?.filter((c) => c.userMatch) ?? []
);

// Show every jurisdiction that matches the user (green chips); only the
// non-matching jurisdictions are rolled into the "+N more" trigger below
// (remainingCount = total − matched). The flyout still lists everything.
const displayedCredits = computed(() => userMatchCredits.value);

const remainingCount = computed(() => {
  const totalCredits = props.credits?.length ?? 0;
  const displayed = displayedCredits.value.length;
  return totalCredits - displayed;
});

// Credit-bearing means at least one credential code is present (CLE/CPE/IAPP/EA
// or any admin-created credential). Microlearning, Skills, and Pathway rows
// fall through the empty array and render a single non-credit chip instead.
const isCreditBearing = computed(() => props.credentialCodes.length > 0);

// One chip per credential code, label resolved from the credential type store.
const credentialChips = computed(() =>
  props.credentialCodes.map((code) => ({
    code,
    label: tagLabel(code) || code.toUpperCase(),
  }))
);

const deliveryChipTitle = computed(() =>
  props.deliveryMode === 'Live'
    ? 'Live webinar'
    : 'On-demand recording'
);

function handleViewJurisdictions() {
  if (props.credits) {
    emit('viewJurisdictions', props.id, props.credits);
  }
}
</script>

<template>
  <div :class="['course-card-row', { 'course-card-row-compact': compact }]">
    <!-- Progress Ring (only for non-compact) -->
    <div v-if="!compact && progress !== undefined" class="course-card-left">
      <ProgressRing :percent="progress" />
    </div>

    <!-- Main Content -->
    <div class="course-card-main">
      <div class="course-card-line1">
        <RouterLink :to="actionUrl" class="course-card-title">{{ title }}</RouterLink>
      </div>
      <div class="course-card-line2">
        <span v-if="dateTime" class="course-card-meta-item">
          <bb-icon name="event" decorative></bb-icon>
          {{ dateTime }}
          <slot name="date-action" />
        </span>
        <span class="course-card-meta-item">
          <bb-icon name="schedule" decorative></bb-icon>
          {{ duration }}
        </span>
        <span v-if="recordedDate" class="course-card-meta-item course-card-recorded-date">
          <bb-icon name="videocam" decorative></bb-icon>
          Recorded {{ formatDate(recordedDate) }}
        </span>
        <span v-if="assignedAt" class="course-card-meta-item">
          <bb-icon name="assignment_ind" decorative></bb-icon>
          Assigned {{ formatDate(assignedAt) }}
        </span>
      </div>
      <div v-if="practiceAreas && practiceAreas.length" class="course-card-line3">
        <span class="course-card-meta-item">
          <bb-icon name="category" decorative></bb-icon>
          {{ practiceAreas.join(', ') }}
        </span>
      </div>
    </div>

    <!-- Credential chip(s) + Delivery chip -->
    <div class="course-card-type">
      <template v-if="isCreditBearing">
        <TypeTag v-for="chip in credentialChips" :key="chip.code" :code="chip.code" size="xs">{{ chip.label }}</TypeTag>
      </template>
      <TypeTag v-else-if="learningObjectTypeCode" :code="learningObjectTypeCode" size="xs" />
      <DeliveryTag v-if="deliveryMode" :mode="deliveryMode" :title="deliveryChipTitle" />
    </div>

    <!-- Credits Column (always render to maintain grid structure) -->
    <div class="course-card-credits">
      <template v-if="isCreditBearing && credits && credits.length">
        <!-- If user has matching jurisdictions: show them + "+X more" for remainder -->
        <template v-if="displayedCredits.length > 0">
          <span
            v-for="credit in displayedCredits"
            :key="credit.label"
            :class="['bb-tag', '-size-xs', credit.userMatch ? '-color-green' : '-neutral']"
          >
            <bb-icon v-if="credit.userMatch" name="check" decorative></bb-icon>{{ credit.label }}
          </span>
          <bb-chip
            v-if="remainingCount > 0"
            clickable
            size="xs"
            variant="neutral"
            icon="location_on"
            :title="`View all ${credits?.length} jurisdictions`"
            @click="handleViewJurisdictions"
          >
            +{{ remainingCount }} more
          </bb-chip>
        </template>
        <!-- If no user matches: just show total count -->
        <bb-chip
          v-else
          clickable
          size="xs"
          variant="neutral"
          icon="location_on"
          title="View approved jurisdictions"
          @click="handleViewJurisdictions"
        >
          {{ credits.length }} jurisdiction{{ credits.length !== 1 ? 's' : '' }}
        </bb-chip>
      </template>
      <!-- Non-credit courses (Skills, Microlearning, etc.) -->
      <span v-else-if="!isCreditBearing" class="bb-tag -size-xs">Non-credit</span>
    </div>

    <!-- Action Button -->
    <div class="course-card-action">
      <RouterLink :to="actionUrl" class="bb-button -table-action -outline">{{ actionLabel }}</RouterLink>
    </div>
  </div>
</template>
